import { Grid, IconButton } from '@mui/joy';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';

import { type BicValueKey } from '~/common/enums/bicValues';
import { type IbanFormikValue } from '@/types/finance';

import { DeleteIcon } from '@/shared/icons/Icons';
import FieldArrayContainer from '@/shared/components/2.0/formik/FieldArrayContainer';
import FormHelperText from '@/shared/components/2.0/forms/FormHelperText';
import FormikCheckbox from '@/shared/components/2.0/formik/FormikCheckbox';
import FormikIbanInput from '@/shared/formik/FormikIbanInput';
import SelectBic from '@/shared/components/2.0/select/SelectBic';

const getEmptyIban = (asDefault: boolean) => ({
  accountNumber: null,
  bic: null,
  isDefault: asDefault,
  tempKey: uuid(),
});

type Props = {
  className?: string;
};

const FormikIbansInput: React.FC<Props> = ({ className }) => {
  const { t } = useTranslation();

  return (
    <FieldArrayContainer
      validateOnChange
      className={className}
      name="ibans"
      renderFooter={({ form }) =>
        form.errors.ibans &&
        !Array.isArray(form.errors.ibans) &&
        form.values?.ibans?.length > 0 && (
          <div className="ml-1 mt-4">
            <FormHelperText touched error={form.errors.ibans as string} />
          </div>
        )
      }
      renderInput={({ form, remove }) =>
        form.values?.ibans?.map((iban: IbanFormikValue, index: number) => (
          <Fragment key={iban.tempKey}>
            <Grid xs={4}>
              <FormikIbanInput
                required
                label={t('accountNumber')}
                name={`ibans[${index}].accountNumber`}
              />
            </Grid>
            <Grid xs={3}>
              <SelectBic
                required
                iban={iban.accountNumber}
                label={t('bic')}
                value={iban.bic as BicValueKey | null}
                onChange={(value) => form.setFieldValue(`ibans[${index}].bic`, value)}
              />
            </Grid>
            <Grid className="mt-8" xs={3}>
              <FormikCheckbox
                validateOnChange
                callback={(value) => {
                  if (!value) {
                    return;
                  }
                  // Set all other ibans to false if this one is true
                  form.setFieldValue(
                    'ibans',
                    form.values.ibans.map((i: IbanFormikValue, x: number) => ({
                      ...i,
                      isDefault: x === index ? value : false,
                    })),
                    true,
                  );
                }}
                label={t('isDefault')}
                name={`ibans[${index}].isDefault`}
              />
            </Grid>
            <Grid marginTop={3} xs={2}>
              <IconButton
                className="w-full"
                color="danger"
                onClick={() => {
                  const ibanToDelete = form.values.ibans.at(index);
                  if (form.values.iban === ibanToDelete.accountNumber) {
                    form.setFieldValue('iban', null);
                  }
                  remove(index);
                }}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Fragment>
        ))
      }
      title={t('ibans')}
      onAdd={({ form, push }) => {
        push(getEmptyIban(!(form.values?.ibans ?? []).find((i: IbanFormikValue) => i.isDefault)));
        form.validateForm();
      }}
    />
  );
};

export default FormikIbansInput;
